import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { DateInput } from "../../../../components/UIToolkit/Inputs/DateInput";
import { SystemAutoComplete } from "../../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { INK } from "../../../../theme/tokens";
import type { CredentialStatus, LocationCredentialResponse } from "../../../../utils/models";
import { SynergyFacilityOptionStartAdornment } from "./SynergyFacilityOptionStartAdornment";
import { useNewCredentialPopoverState } from "./useNewCredentialPopoverState";

type Props = {
	onSubmit: (locationId: string, expiresAt: string | null, nurseIds: number[], status: CredentialStatus) => void;
	onCancel: () => void;
	isSubmitting: boolean;
	defaultLocationId?: string;
	defaultNurseId?: number;
	nurseSelectionMode: "single" | "multiple";
	/**
	 * If we want to not show nurses that already have credentials for the selected location
	 */
	credentials?: LocationCredentialResponse[];
};

export const NewCredentialPopover: FC<Props> = ({ onSubmit, onCancel, isSubmitting, defaultLocationId, defaultNurseId, nurseSelectionMode, credentials }) => {
	const { t } = useTranslation();
	const {
		selectedLocation,
		selectedNurses,
		expiresAt,
		status,
		isValid,
		handleLocationChange,
		handleNurseChangeSingle,
		handleNurseChangeMultiple,
		handleExpiresAtChange,
		handleStatusChange,
		autocompleteOptions,
		nurseDropdownItems,
		statusDropdownItems,
		nurseItemToString,
	} = useNewCredentialPopoverState({ defaultLocationId, defaultNurseId, credentials });

	const handleSubmit = () => {
		if (isValid && selectedLocation && selectedNurses && selectedNurses.length > 0) {
			onSubmit(
				selectedLocation.value.id,
				expiresAt?.toISO() ?? null,
				selectedNurses.map((nurse) => nurse.id),
				status,
			);
		}
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 2,
				minWidth: "500px",
			}}
		>
			<Typography
				variant="h6"
				sx={{
					fontWeight: 600,
					fontSize: "16px",
					color: INK.PRIMARY,
				}}
			>
				{t("credential.new.title", { defaultValue: "Add New Credential" })}
			</Typography>
			<SystemAutoComplete
				fullWidth={true}
				onChange={handleLocationChange}
				value={selectedLocation}
				options={autocompleteOptions}
				textFieldProps={{
					placeholder: "Type Facility name/type or Section",
					label: "Facility",
					reserveHeightForHelperText: false,
				}}
				getOptionStartAdornment={(option) => <SynergyFacilityOptionStartAdornment facilityType={option.value.departmentType} />}
				justifyContent={"start"}
				disabled={!!defaultLocationId}
			/>
			{nurseSelectionMode === "multiple" ? (
				<SystemDropdown
					multiple={true}
					showFilter={true}
					items={nurseDropdownItems}
					selectedItems={selectedNurses}
					onChangeMultiple={handleNurseChangeMultiple}
					disabled={!!defaultNurseId}
					valueToString={nurseItemToString}
					label={"Select Staff Members"}
					maxVisibleChips={3}
				/>
			) : (
				<SystemDropdown
					multiple={false}
					showFilter={true}
					items={nurseDropdownItems}
					selectedItem={selectedNurses.length > 0 ? selectedNurses[0] : null}
					onChange={handleNurseChangeSingle}
					disabled={!!defaultNurseId}
					valueToString={nurseItemToString}
					label={"Select Staff Member"}
				/>
			)}
			<SystemDropdown
				multiple={false}
				items={statusDropdownItems}
				selectedItem={status}
				onChange={handleStatusChange}
				valueToString={(value) => value || ""}
				label={"Status"}
			/>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: "8px",
				}}
			>
				<Typography
					variant="body2"
					sx={{
						fontWeight: 500,
						fontSize: "14px",
						color: INK.SECONDARY,
					}}
				>
					{t("credential.expires_at.label", { defaultValue: "Expiration Date (Optional)" })}
				</Typography>
				<DateInput
					value={expiresAt ?? undefined}
					onChange={handleExpiresAtChange}
					placeholder={t("credential.expires_at.placeholder", { defaultValue: "Select date" })}
					minDate={DateTime.now().startOf("day")}
				/>
			</Box>
			<Box
				sx={{
					display: "flex",
					gap: "12px",
					justifyContent: "flex-end",
					marginTop: "8px",
				}}
			>
				<SystemButton buttonVariant="secondary" onClick={onCancel} disabled={isSubmitting}>
					{t("common.cancel", { defaultValue: "Cancel" })}
				</SystemButton>
				<SystemButton buttonVariant="primary" onClick={handleSubmit} disabled={!isValid || isSubmitting} loading={isSubmitting}>
					{t("common.submit", { defaultValue: "Submit" })}
				</SystemButton>
			</Box>
		</Box>
	);
};
